import { stripTerminalCredentials } from "@/lib/seo-meta";

// SEO-friendly slug helpers for provider URLs.
// Preferred format: {firstname}{lastname}{npi} — no separators, ends in the
// provider's 10-digit NPI (unique, so the name prefix is cosmetic only).
// Providers with no NPI on file fall back to the legacy
// {name}-{specialty}-{city}-{state}-{publicId} format, where publicId is the
// provider's HL####### listing ID (or first 8 chars of UUID for older rows
// not yet backfilled by the DB trigger).

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PUBLIC_ID_RE = /^HL\d{7}$/i;
const NPI_RE = /^\d{10}$/;

function slugify(input: string | null | undefined): string {
  if (!input) return "";
  return input
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

export function shortIdFromUuid(id: string): string {
  return id.replace(/-/g, "").slice(0, 8).toLowerCase();
}

export function buildProviderSlug(p: {
  id: string;
  public_id?: string | null;
  name?: string | null;
  specialty?: string | null;
  city?: string | null;
  state?: string | null;
  npi?: string | null;
}): string {
  const npi = p.npi?.trim();
  if (npi && NPI_RE.test(npi)) {
    const nameParts = stripTerminalCredentials(p.name ?? "")
      .replace(/^Dr\.?\s+/i, "")
      .split(/[\s,]+/)
      .filter(Boolean);
    const first = nameParts[0] ?? "";
    const last = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
    const namePart = slugify(`${first} ${last}`).replace(/-/g, "");
    return `${namePart}${npi}`;
  }
  const parts = [p.name, p.specialty, p.city, p.state]
    .map((s) => slugify(s))
    .filter(Boolean)
    .join("-");
  // Prefer the human-readable HL####### public_id; fall back to UUID short.
  const tail = p.public_id && PUBLIC_ID_RE.test(p.public_id)
    ? p.public_id.toUpperCase()
    : shortIdFromUuid(p.id);
  return parts ? `${parts}-${tail}` : tail;
}

export type ParsedProviderParam =
  | { kind: "uuid"; uuid: string }
  | { kind: "npi"; npi: string; slug: string }
  | { kind: "public_id"; publicId: string; slug: string }
  | { kind: "slug"; shortId: string; slug: string }
  | { kind: "invalid" };

export function parseProviderParam(raw: string): ParsedProviderParam {
  const s = (raw || "").trim();
  if (!s) return { kind: "invalid" };
  if (UUID_RE.test(s)) return { kind: "uuid", uuid: s.toLowerCase() };
  // Slug with trailing HL####### segment (legacy, still resolvable so old
  // indexed links 301 to the new canonical {name}{npi} slug).
  const pub = s.match(/-(HL\d{7})$/i);
  if (pub) return { kind: "public_id", publicId: pub[1].toUpperCase(), slug: s };
  // Bare HL####### with no slug text.
  if (PUBLIC_ID_RE.test(s)) return { kind: "public_id", publicId: s.toUpperCase(), slug: s };
  // Legacy slug: extract trailing 8-char hex segment.
  const m = s.match(/-([0-9a-f]{8})$/i);
  if (m) return { kind: "slug", shortId: m[1].toLowerCase(), slug: s };
  // Bare 8-char hex (no slug text).
  if (/^[0-9a-f]{8}$/i.test(s)) return { kind: "slug", shortId: s.toLowerCase(), slug: s };
  // Preferred slug: {name}{npi}, no separator before the trailing 10 digits.
  const npi = s.match(/(\d{10})$/);
  if (npi) return { kind: "npi", npi: npi[1], slug: s };
  return { kind: "invalid" };
}
